import React, { useState, useEffect, useCallback } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  useTheme,
  useMediaQuery,
  Snackbar,
  Alert,
  CircularProgress,
  IconButton,
  Grid2,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Select,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import EditIcon from '@mui/icons-material/Edit'
import VisibilityIcon from '@mui/icons-material/Visibility'
import SearchIcon from '@mui/icons-material/Search'
import UploadIcon from '@mui/icons-material/Upload'
import { useNavigate } from 'react-router-dom'
import userService, { User, PaginationParams } from '../services/userService'
import CommonPagination from '../components/common/CommonPagination'
import PageHeader from '../components/common/PageHeader'
import FilterGrid2 from '../components/common/Filters/FilterGrid2'
import SearchInput from '../components/common/Filters/SearchInput'
import SearchButton from '../components/common/Filters/SearchButton'
import SingleSelectFilter from '../components/common/Filters/SingleSelectFilter'
import AddButton from '../components/common/Filters/AddButton'
import UploadModal from '../components/modals/UploadModal'

const Employees: React.FC = () => {
  const theme = useTheme()
  const navigate = useNavigate()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.down('md'))

  const [employees, setEmployees] = useState<User[]>([])
  const [loading, setLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState<string>('')
  const [filter, setFilter] = useState<any>({
    roles: ['employee'],
  })
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [sortByFilter, setSortByFilter] = useState<string>('')
  const [page, setPage] = useState<number>(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(10)
  const [totalEmployees, setTotalEmployees] = useState<number>(0)

  const [snackbar, setSnackbar] = useState<{
    open: boolean
    message: string
    severity: 'success' | 'error'
  }>({
    open: false,
    message: '',
    severity: 'success',
  })

  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  const fetchEmployees = useCallback(async () => {
    setLoading(true)
    try {
      const params: PaginationParams = {
        page: page + 1, // API uses 1-based indexing
        perPage: rowsPerPage,
        ...filter,
      }

      const { users: fetchedEmployees, pagination } =
        await userService.getUsers(params)
      setEmployees(fetchedEmployees)
      setTotalEmployees(pagination.total)
      setError(null)
    } catch (err) {
      console.error('Error fetching employees:', err)
      setError('ไม่สามารถโหลดข้อมูลพนักงานได้ กรุณาลองใหม่อีกครั้ง')
      setSnackbar({
        open: true,
        message: 'ไม่สามารถโหลดข้อมูลพนักงานได้ กรุณาลองใหม่อีกครั้ง',
        severity: 'error',
      })
    } finally {
      setLoading(false)
    }
  }, [page, rowsPerPage, filter])

  useEffect(() => {
    fetchEmployees()
  }, [fetchEmployees])

  const handleAddEmployee = () => {
    navigate('/users/add?from=employee')
  }

  const handleUploadEmployee = () => {
    setUploadModalOpen(true);
  }

  const handleEditEmployee = (username: string) => {
    navigate(`/users/${username}/edit`, { state: { from: 'employees' } })
  }

  const handleViewCheckInOut = (username: string) => {
    navigate(`/employees/${username}/checkinout`)
  }

  const handleSearch = () => {
    // setSearchTerm(searchValue);
    setFilter({
      search: searchTerm,
      roles: ['employee'],
      status: statusFilter,
      sortBy: sortByFilter,
      sortOrder: 'asc',
    })
    setPage(0) // Reset to first page on new search
  }

  const handleFilterChange = (filterName: string, value: string) => {
    if (filterName === 'status') {
      setStatusFilter(value)
    }
    setPage(0) // Reset to first page on filter change
  }

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }))
  }

  const handleChangePage = (newPage: number) => {
    setPage(newPage)
  }

  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  const handleCloseUploadModal = () => {
    setUploadModalOpen(false);
  };

  const handleFileUpload = async (file: File) => {
    try {
      // TODO: Implement file upload logic here
      setSnackbar({
        open: true,
        message: 'อัพโหลดไฟล์สำเร็จ',
        severity: 'success',
      })
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'เกิดข้อผิดพลาดในการอัพโหลดไฟล์',
        severity: 'error',
      })
    }
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3, md: 4 },
        maxWidth: '100%',
        bgcolor: '#FFFFFF',
      }}
    >
      <PageHeader title="รายชื่อพนักงาน" />

      <FilterGrid2>
        <Grid2 size={{ xs: 12, sm: 3 }}>
          <SearchInput
            title="ค้นหาพนักงาน"
            value={searchTerm}
            placeholder="ค้นหารายชื่อพนักงาน หรือรหัสพนักงาน"
            autoChange
            onChange={(value) => setSearchTerm(value)}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 2 }}>
          <SingleSelectFilter
            filter={{
              label: 'สถานะการใช้งาน',
              value: 'status',
              options: [
                { value: 'active', label: 'ใช้งาน' },
                { value: 'inactive', label: 'ไม่ใช้งาน' },
              ],
            }}
            value={statusFilter}
            onChange={(value) => setStatusFilter(value.target.value as string)}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, sm: 2 }}>
          <SingleSelectFilter
            filter={{
              label: 'เรียง',
              value: 'sortBy',
              options: [
                { value: 'name', label: 'ชื่อพนักงาน' },
                { value: 'employeeCode', label: 'รหัสพนักงาน' },
              ],
            }}
            defaultText="เรียงลำดับ"
            value={sortByFilter}
            onChange={(value) => setSortByFilter(value.target.value as string)}
          />
        </Grid2>
        <Grid2>
          <SearchButton onClick={() => handleSearch()} />
        </Grid2>
        <Grid2 container direction="row" sx={{ marginLeft: { xs: 0, md: 0, lg: 'auto' } }}>
          <Grid2>
          <AddButton onClick={handleAddEmployee} label="เพิ่มรายชื่อพนักงาน" />
          </Grid2>
          <Grid2>
          <AddButton onClick={handleUploadEmployee} label="อับโหลดรายชื่อพนักงาน" startIcon={<UploadIcon />} />
          </Grid2>
        </Grid2>

      </FilterGrid2>

      <Paper
        sx={{
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {loading ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: 400,
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell align="center">ลำดับ</TableCell>
                  <TableCell align="center">รูปภาพ</TableCell>
                  <TableCell align="center">รหัสพนักงาน</TableCell>
                  <TableCell>ชื่อพนักงาน</TableCell>
                  {/* <TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '1rem' }}>สถิติการเข้าออกงาน</TableCell> */}
                  <TableCell align="center">Username</TableCell>
                  <TableCell align="center">เบอร์โทร</TableCell>
                  <TableCell align="center">สถานะการใช้งาน</TableCell>
                  <TableCell align="center">ดูรายละเอียด/แก้ไข</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.length > 0 ? (
                  employees.map((employee, index) => (
                    <TableRow
                      key={employee._id}
                      sx={{
                        '&:nth-of-type(odd)': { backgroundColor: '#fafafa' },
                        '&:hover': { backgroundColor: '#f0f0f0' },
                        height: '60px',
                      }}
                    >
                      <TableCell align="center">
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          component="img"
                          src={
                            employee.image || '/images/avatar-placeholder.jpg'
                          }
                          alt={employee.name || 'Avatar'}
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '50%',
                            objectFit: 'cover',
                          }}
                        />
                      </TableCell>
                      <TableCell align="center">
                        {employee.employeeCode || '-'}
                      </TableCell>
                      <TableCell>{employee.name || '-'}</TableCell>
                      {/* <TableCell align="center">0</TableCell> */}
                      <TableCell align="center">
                        {employee.username || '-'}
                      </TableCell>
                      <TableCell align="center">
                        {employee.phoneNumber || '-'}
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'inline-block',
                            color: employee.isEnable ? '#2e7d32' : '#d32f2f',
                            fontSize: '0.9rem',
                            fontWeight: 'medium',
                            textAlign: 'center',
                          }}
                        >
                          {employee.isEnable ? 'เปิดการใช้งาน' : 'ปิดการใช้งาน'}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          <IconButton
                            onClick={() =>
                              handleViewCheckInOut(employee.username)
                            }
                          >
                            <Box
                              component="img"
                              src="/images/icons/button-detail.png"
                              alt="View"
                              sx={{ width: 32, height: 32 }}
                            />
                          </IconButton>
                          <IconButton
                            onClick={() =>
                              handleEditEmployee(employee.username)
                            }
                          >
                            <Box
                              component="img"
                              src="/images/icons/button-modify.png"
                              alt="Edit"
                              sx={{ width: 32, height: 32 }}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5 }}>
                      <Typography variant="h6" color="text.secondary">
                        ไม่พบข้อมูลพนักงาน
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <select
              value={rowsPerPage}
              onChange={(e) => handleChangeRowsPerPage(Number(e.target.value))}
              style={{
                padding: "4px 8px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
                marginRight: "16px",
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>

            <Box sx={{ display: "flex", gap: 0.5 }}>
              <Button
                disabled={page === 0}
                onClick={() => handleChangePage(0)}
                sx={{
                  minWidth: "auto",
                  px: 1,
                  py: 0.5,
                  color: page === 0 ? "text.disabled" : "primary.main",
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  bgcolor: page === 0 ? "transparent" : "#f5f5f5",
                }}
              >
                1
              </Button>

              {page > 1 && (
                <Button
                  onClick={() => handleChangePage(page - 1)}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.5,
                    color: "primary.main",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {page}
                </Button>
              )}

              {page > 0 && (
                <Button
                  onClick={() => handleChangePage(page)}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.5,
                    color: "primary.main",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {page + 1}
                </Button>
              )}

              {page < Math.ceil(totalEmployees / rowsPerPage) - 1 && (
                <Button
                  onClick={() => handleChangePage(page + 1)}
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.5,
                    color: "primary.main",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {page + 2}
                </Button>
              )}

              {page < Math.ceil(totalEmployees / rowsPerPage) - 2 && (
                <Button
                  onClick={() =>
                    handleChangePage(
                      Math.ceil(totalEmployees / rowsPerPage) - 1
                    )
                  }
                  sx={{
                    minWidth: "auto",
                    px: 1,
                    py: 0.5,
                    color: "primary.main",
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    bgcolor: "#f5f5f5",
                  }}
                >
                  {Math.ceil(totalEmployees / rowsPerPage)}
                </Button>
              )}
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            กำลังแสดง {page * rowsPerPage + 1} ถึง{" "}
            {Math.min((page + 1) * rowsPerPage, totalEmployees)} จาก{" "}
            {totalEmployees} รายการ
          </Typography>
        </Box> */}
      </Paper>
      <CommonPagination
        count={Math.ceil(totalEmployees / rowsPerPage)}
        page={page + 1}
        rowsPerPage={rowsPerPage}
        onChange={(event, value) => {
          handleChangePage(value - 1)
        }}
        onRowsPerPageChange={(value) => {
          handleChangeRowsPerPage(value)
        }}
        totalItems={totalEmployees}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      <UploadModal
        open={uploadModalOpen}
        onClose={handleCloseUploadModal}
        onUpload={handleFileUpload}
        title="อัพโหลดรายชื่อพนักงาน"
        dropzoneText="ลากไฟล์ Excel มาวางที่นี่ หรือคลิกเพื่อเลือกไฟล์"
        onUploadResult={(result) => setSnackbar({ open: true, ...result })}
      />
    </Box>
  )
}

export default Employees
